import { MdCheck, MdKeyboardArrowDown } from 'react-icons/md';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';
import { FlagDE, FlagFR, FlagIT, FlagMC, FlagNL } from './icons/flags';

export type DialCode = {
  /** Code ISO, sert de clé. */
  iso: string;
  label: string;
  dial: string;
  flag: ReactNode;
};

/**
 * Pays couverts par Doctolib, France en tête (marché principal).
 * Monaco partage la numérotation avec la France mais garde son propre indicatif.
 */
export const DIAL_CODES: readonly DialCode[] = [
  { iso: 'FR', label: 'France', dial: '+33', flag: <FlagFR /> },
  { iso: 'DE', label: 'Allemagne', dial: '+49', flag: <FlagDE /> },
  { iso: 'IT', label: 'Italie', dial: '+39', flag: <FlagIT /> },
  { iso: 'NL', label: 'Pays-Bas', dial: '+31', flag: <FlagNL /> },
  { iso: 'MC', label: 'Monaco', dial: '+377', flag: <FlagMC /> },
];

export const DEFAULT_DIAL_CODE = '+33';

export type PhoneInputProps = Omit<ComponentPropsWithRef<'input'>, 'size' | 'type'> & {
  invalid?: boolean;
  /** Indicatif sélectionné, au format `+33`. */
  dialCode: string;
  onDialCodeChange: (dialCode: string) => void;
  onDialCodeBlur?: () => void;
  containerClassName?: string;
};

/** Champ téléphone : indicatif à gauche (France par défaut), numéro à droite. */
export function PhoneInput({
  className,
  containerClassName,
  invalid,
  dialCode,
  onDialCodeChange,
  onDialCodeBlur,
  disabled,
  ...props
}: PhoneInputProps) {
  return (
    <div
      className={cn(
        'flex h-[53px] w-full items-center rounded-field border bg-surface transition-colors',
        'border-border-input/50 focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600/15',
        invalid && 'border-danger focus-within:border-danger focus-within:ring-danger/15',
        disabled && 'cursor-not-allowed bg-surface-muted opacity-60',
        containerClassName,
      )}
    >
      <DialCodeSelect
        value={dialCode}
        onChange={onDialCodeChange}
        onBlur={onDialCodeBlur}
        disabled={disabled}
      />

      <span aria-hidden="true" className="h-6 w-px shrink-0 bg-border-input/30" />

      <input
        type="tel"
        inputMode="tel"
        aria-invalid={invalid || undefined}
        disabled={disabled}
        className={cn(
          'h-full w-full min-w-0 rounded-r-field bg-transparent px-3 text-sm font-medium text-content outline-none',
          'placeholder:font-medium placeholder:text-content-subtle disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      />
    </div>
  );
}

type DialCodeSelectProps = {
  value: string;
  onChange: (dial: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
};

/**
 * Liste déroulante maison : un `<select>` natif ne peut pas afficher de SVG
 * dans ses `<option>`, d'où ce bouton + listbox.
 */
function DialCodeSelect({ value, onChange, onBlur, disabled }: DialCodeSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const selected = DIAL_CODES.find((country) => country.dial === value) ?? DIAL_CODES[0];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const select = (dial: string) => {
    onChange(dial);
    setOpen(false);
    onBlur?.();
  };

  return (
    <div ref={containerRef} className="relative h-full shrink-0">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={`Indicatif téléphonique : ${selected?.label ?? ''}`}
        onClick={() => setOpen((current) => !current)}
        className="flex h-full items-center gap-2 rounded-l-field pl-3 pr-2 text-sm font-medium text-content outline-none transition-colors hover:bg-surface-muted focus-visible:bg-surface-muted disabled:cursor-not-allowed"
      >
        {selected?.flag}
        {selected?.dial}
        <MdKeyboardArrowDown
          aria-hidden="true"
          className={cn('size-4 text-content-subtle transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Indicatif téléphonique"
          className="absolute left-0 top-[calc(100%+0.5rem)] z-20 min-w-[15rem] overflow-hidden rounded-field border border-border-subtle bg-surface py-1 shadow-card"
        >
          {DIAL_CODES.map((country) => {
            const isSelected = country.dial === value;
            return (
              <li key={country.iso}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => select(country.dial)}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-brand-50',
                    isSelected ? 'font-semibold text-brand-600' : 'text-content',
                  )}
                >
                  {country.flag}
                  <span className="flex-1">{country.label}</span>
                  <span className="text-content-subtle">{country.dial}</span>
                  {isSelected ? (
                    <MdCheck aria-hidden="true" className="size-4 text-brand-600" />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
